import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../components/Navbar';
import { Bug, MagnifyingGlass, Funnel, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PestIdentificationGuide() {
  const [pests, setPests] = useState([]);
  const [filteredPests, setFilteredPests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedRiskLevel, setSelectedRiskLevel] = useState('all');
  const [selectedPest, setSelectedPest] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const fetchPests = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/pests`);
      if (!res.ok) throw new Error('Failed to fetch pests');
      const data = await res.json();
      setPests(data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load pest database');
      setLoading(false);
    }
  }, []);

  const filterPests = useCallback(() => {
    let filtered = [...pests];

    if (searchTerm) {
      filtered = filtered.filter(pest =>
        pest.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pest.scientific_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pest.identification_features.some(feature => 
          feature.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(pest => pest.category === selectedCategory);
    }

    if (selectedRiskLevel !== 'all') {
      filtered = filtered.filter(pest => pest.risk_level === selectedRiskLevel);
    }

    setFilteredPests(filtered);
  }, [pests, searchTerm, selectedCategory, selectedRiskLevel]);

  useEffect(() => {
    fetchPests();
  }, [fetchPests]);

  useEffect(() => {
    filterPests();
  }, [filterPests]);

  const getRiskColor = (level) => {
    switch(level) {
      case 'low': return 'bg-green-100 text-green-800';
      case 'moderate': return 'bg-yellow-100 text-yellow-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'severe': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category) => {
    return <Bug size={24} weight="fill" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl text-text_dark">Loading pest database...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-primary text-text_light py-12 border-b border-borders">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-4">
            <Bug size={48} weight="fill" className="text-accent" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold">
              Pest Identification & Treatment Guide
            </h1>
          </div>
          <p className="text-base sm:text-lg max-w-3xl">
            Comprehensive database of common Pennsylvania pests with expert identification tips, life cycle information, treatment methods, and safety guidelines.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Search and Filter Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-borders p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1 relative">
              <MagnifyingGlass size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text_muted" />
              <input
                type="text"
                placeholder="Search by pest name, scientific name, or features..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* Filter Toggle Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-sm hover:bg-primary/90 transition-colors"
            >
              <Funnel size={20} />
              Filters
            </button>
          </div>

          {/* Filter Options */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-borders grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text_dark mb-2">Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-2 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">All Categories</option>
                  <option value="insect">Insects</option>
                  <option value="rodent">Rodents</option>
                  <option value="arachnid">Arachnids</option>
                  <option value="wildlife">Wildlife</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text_dark mb-2">Risk Level</label>
                <select
                  value={selectedRiskLevel}
                  onChange={(e) => setSelectedRiskLevel(e.target.value)}
                  className="w-full px-4 py-2 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">All Risk Levels</option>
                  <option value="low">Low</option>
                  <option value="moderate">Moderate</option>
                  <option value="high">High</option>
                  <option value="severe">Severe</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Results Count */}
        <div className="mb-4 text-text_muted">
          Found {filteredPests.length} pest{filteredPests.length !== 1 ? 's' : ''}
        </div>

        {/* Pest Grid */}
        {filteredPests.length === 0 ? (
          <div className="text-center py-12 text-text_muted">
            No pests found matching your criteria. Try adjusting your filters.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPests.map((pest) => (
              <div
                key={pest.id}
                onClick={() => setSelectedPest(pest)}
                className="bg-white rounded-lg shadow-sm border border-borders p-6 hover:shadow-md transition-shadow cursor-pointer"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="text-primary">{getCategoryIcon(pest.category)}</div>
                    <div>
                      <h3 className="font-semibold text-lg text-text_dark">{pest.name}</h3>
                      <p className="text-sm text-text_muted italic">{pest.scientific_name}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getRiskColor(pest.risk_level)}`}>
                    {pest.risk_level.toUpperCase()} RISK
                  </span>
                  
                  <p className="text-sm text-text_dark line-clamp-2">
                    <strong>Habitat:</strong> {pest.habitat}
                  </p>

                  <p className="text-sm text-primary font-medium">
                    Click to view full details →
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pest Detail Modal */}
      {selectedPest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-primary text-white p-6 flex items-start justify-between">
              <div>
                <h2 className="text-3xl font-bold mb-2">{selectedPest.name}</h2>
                <p className="text-lg italic opacity-90">{selectedPest.scientific_name}</p>
                <div className="mt-2 flex items-center gap-2">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getRiskColor(selectedPest.risk_level)}`}>
                    {selectedPest.risk_level.toUpperCase()} RISK
                  </span>
                  <span className="px-3 py-1 bg-white/20 rounded-full text-xs font-medium uppercase">
                    {selectedPest.category}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedPest(null)}
                className="text-white hover:bg-white/20 rounded-sm p-2"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Identification - Fixed key */}
              <section>
                <h3 className="text-xl font-bold text-primary mb-3">🔍 Identification Features</h3>
                <ul className="list-disc list-inside space-y-1 text-text_dark">
                  {selectedPest.identification_features.map((feature) => (
                    <li key={`feature-${feature.substring(0, 20)}`}>{feature}</li>
                  ))}
                </ul>
              </section>

              {/* Habitat & Behavior */}
              <section className="grid md:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-xl font-bold text-primary mb-3">🏡 Habitat</h3>
                  <p className="text-text_dark">{selectedPest.habitat}</p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-primary mb-3">🐾 Behavior</h3>
                  <p className="text-text_dark">{selectedPest.behavior}</p>
                </div>
              </section>

              {/* Signs of Infestation - Fixed key */}
              <section>
                <h3 className="text-xl font-bold text-primary mb-3">⚠️ Signs of Infestation</h3>
                <ul className="list-disc list-inside space-y-1 text-text_dark">
                  {selectedPest.signs_of_infestation.map((sign) => (
                    <li key={`sign-${sign.substring(0, 20)}`}>{sign}</li>
                  ))}
                </ul>
              </section>

              {/* Life Cycle - Fixed key */}
              <section>
                <h3 className="text-xl font-bold text-primary mb-3">🔄 Life Cycle Stages</h3>
                <div className="grid gap-3">
                  {selectedPest.life_cycle_stages.map((stage) => (
                    <div key={`stage-${stage.stage}`} className="bg-gray-50 p-4 rounded-sm border border-borders">
                      <h4 className="font-semibold text-text_dark mb-1">{stage.stage}</h4>
                      <p className="text-sm text-text_muted">{stage.description}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Treatment Methods - Fixed keys */}
              <section>
                <h3 className="text-xl font-bold text-primary mb-3">💊 Treatment Methods</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-text_dark mb-2">Chemical Treatments:</h4>
                    <ul className="list-disc list-inside space-y-1 text-sm text-text_dark">
                      {selectedPest.treatment_methods.chemical.map((method) => (
                        <li key={`chem-${method.substring(0, 20)}`}>{method}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-text_dark mb-2">Non-Chemical Treatments:</h4>
                    <ul className="list-disc list-inside space-y-1 text-sm text-text_dark">
                      {selectedPest.treatment_methods.non_chemical.map((method) => (
                        <li key={`nonchem-${method.substring(0, 20)}`}>{method}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-text_dark mb-2">IPM (Integrated Pest Management):</h4>
                    <ul className="list-disc list-inside space-y-1 text-sm text-text_dark">
                      {selectedPest.treatment_methods.ipm.map((method) => (
                        <li key={`ipm-${method.substring(0, 20)}`}>{method}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>

              {/* Prevention Tips - Fixed key */}
              <section>
                <h3 className="text-xl font-bold text-primary mb-3">🛡️ Prevention Tips</h3>
                <ul className="list-disc list-inside space-y-1 text-text_dark">
                  {selectedPest.prevention_tips.map((tip) => (
                    <li key={`tip-${tip.substring(0, 20)}`}>{tip}</li>
                  ))}
                </ul>
              </section>

              {/* Regional & Seasonal Info */}
              <section className="grid md:grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-sm border border-blue-200">
                  <h3 className="text-lg font-bold text-primary mb-2">📍 PA Regional Notes</h3>
                  <p className="text-sm text-text_dark">{selectedPest.regional_notes}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-sm border border-green-200">
                  <h3 className="text-lg font-bold text-primary mb-2">📅 Seasonal Activity</h3>
                  <p className="text-sm text-text_dark">{selectedPest.seasonal_activity}</p>
                </div>
              </section>

              {/* Health & Property Risks - Fixed keys */}
              <section className="grid md:grid-cols-2 gap-4">
                <div className="bg-red-50 p-4 rounded-sm border border-red-200">
                  <h3 className="text-lg font-bold text-red-700 mb-2">⚕️ Health Risks</h3>
                  <ul className="list-disc list-inside space-y-1 text-sm text-text_dark">
                    {selectedPest.health_risks.map((risk) => (
                      <li key={`health-${risk.substring(0, 20)}`}>{risk}</li>
                    ))}
                  </ul>
                </div>
                <div className="bg-orange-50 p-4 rounded-sm border border-orange-200">
                  <h3 className="text-lg font-bold text-orange-700 mb-2">🏠 Property Risks</h3>
                  <ul className="list-disc list-inside space-y-1 text-sm text-text_dark">
                    {selectedPest.property_risks.map((risk) => (
                      <li key={`prop-${risk.substring(0, 20)}`}>{risk}</li>
                    ))}
                  </ul>
                </div>
              </section>

              {/* Safety Warnings - Fixed key */}
              <section className="bg-yellow-50 p-4 rounded-sm border-2 border-yellow-400">
                <h3 className="text-lg font-bold text-yellow-800 mb-2">⚠️ Safety Warnings</h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-text_dark">
                  {selectedPest.safety_warnings.map((warning) => (
                    <li key={`warn-${warning.substring(0, 20)}`}>{warning}</li>
                  ))}
                </ul>
              </section>

              {/* Regulations */}
              {selectedPest.regulations && (
                <section className="bg-gray-50 p-4 rounded-sm border border-borders">
                  <h3 className="text-lg font-bold text-primary mb-2">📋 PA Regulations</h3>
                  <p className="text-sm text-text_dark">{selectedPest.regulations}</p>
                </section>
              )}
            </div>

            <div className="sticky bottom-0 bg-gray-100 p-4 border-t border-borders">
              <button
                onClick={() => setSelectedPest(null)}
                className="w-full px-6 py-3 bg-primary text-white rounded-sm hover:bg-primary/90 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
